import { useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  User,
  Image as ImageIcon,
  ShoppingBag,
  DollarSign,
  Heart,
  Settings,
  LogOut,
  Plus,
  ChevronRight,
  X,
  Compass,
  Users,
  Newspaper,
  Lightbulb,
  Check,
  AlertCircle,
} from 'lucide-react';
import { GlassSurface } from './GlassSurface';
import { Button } from './Button';
import { overlay_variants, modal_variants } from '../../utils/animations';
import { get_avatar_url, AVATAR_SIZES } from '../../utils/formatters';

/**
 * MenuPanel - Panel híbrido de navegación y usuario
 *
 * Mobile: Layout vertical con secciones NAVEGAR + GESTIONAR
 * Desktop (lg+): Layout horizontal expandido tipo dashboard
 *
 * Soporta usuarios autenticados y no autenticados
 *
 * @param {Object} props
 * @param {boolean} props.is_open - Estado de visibilidad
 * @param {Function} props.on_close - Callback al cerrar
 * @param {Object} props.user - Datos del usuario (null si no autenticado).
 *   `avatar_url` debe ser la URL CRUDA (sin transformar) — MenuPanel aplica
 *   get_avatar_url con el width correcto por breakpoint (mobile/desktop).
 * @param {boolean} props.is_authenticated - Si el usuario está autenticado
 * @param {Object} props.stats - Estadísticas del usuario
 * @param {Array} props.nav_items - Items de navegación principal
 * @param {Function} props.on_navigate - Callback de navegación (recibe path)
 * @param {Function} props.on_logout - Callback de cerrar sesión
 * @param {Function} props.on_login - Callback para ir a login
 */
export function MenuPanel({
  is_open = false,
  on_close,
  user = null,
  is_authenticated = false,
  stats = {},
  nav_items = [],
  on_navigate,
  on_logout,
  on_login,
  profile_setup_complete = true,
}) {
  // Datos por defecto
  const default_user = user ? {
    name: 'Usuario',
    username: 'usuario',
    avatar_url: null,
    is_verified: false,
    is_artist: false,
    ...user,
  } : null;

  const default_stats = {
    obras: 0,
    ventas: '$0',
    likes: 0,
    ...stats,
  };

  // Items de navegación con iconos
  const nav_items_with_icons = [
    { icon: Compass, label: 'Explorar', path: '/explorar', color: 'text-raios-primary' },
    { icon: Users, label: 'Artistas', path: '/artistas', color: 'text-raios-tertiary' },
    { icon: Newspaper, label: 'Noticias', path: '/noticias', color: 'text-green-400' },
    { icon: Lightbulb, label: 'Propuestas', path: '/propuestas', color: 'text-yellow-400' },
  ];

  // Manejo de ESC
  const handle_key_down = useCallback(
    (event) => {
      if (event.key === 'Escape' && on_close) {
        on_close();
      }
    },
    [on_close]
  );

  // Back gesture: pushState so mobile "back" closes the panel instead of navigating.
  // Key-based check: only call history.back() if our entry is still current
  // (prevents undoing link navigations when user clicks a nav item).
  // Deferred cleanup: history.back() is deferred via setTimeout so React StrictMode's
  // mount→unmount→remount cycle can cancel it before it fires.
  const history_key_ref = useRef(null);
  const cleanup_timer_ref = useRef(null);

  useEffect(() => {
    if (is_open) {
      // Cancel any pending cleanup from StrictMode's unmount cycle
      if (cleanup_timer_ref.current) {
        clearTimeout(cleanup_timer_ref.current);
        cleanup_timer_ref.current = null;
      }

      document.addEventListener('keydown', handle_key_down);
      document.body.style.overflow = 'hidden';

      const state_key = `menu_${Date.now()}`;
      window.history.pushState({ panel: 'menu', key: state_key }, '');
      history_key_ref.current = state_key;

      const handle_popstate = () => {
        // Browser already went back — just close, don't touch history
        history_key_ref.current = null;
        on_close?.();
      };
      window.addEventListener('popstate', handle_popstate);

      return () => {
        document.removeEventListener('keydown', handle_key_down);
        document.body.style.overflow = '';
        window.removeEventListener('popstate', handle_popstate);
        const key_to_clean = history_key_ref.current;
        history_key_ref.current = null;
        if (key_to_clean && window.history.state?.key === key_to_clean) {
          // Defer so StrictMode remount can cancel before it fires
          cleanup_timer_ref.current = setTimeout(() => {
            cleanup_timer_ref.current = null;
            if (window.history.state?.key === key_to_clean) {
              window.history.back();
            }
          }, 0);
        }
      };
    }

    return () => {
      document.removeEventListener('keydown', handle_key_down);
      document.body.style.overflow = '';
    };
  }, [is_open, handle_key_down, on_close]);

  const handle_navigate = (path) => {
    on_navigate?.(path);
    on_close?.();
  };

  // Acciones principales (solo para usuarios autenticados)
  const main_actions = [
    { icon: ImageIcon, label: 'Mis Obras', path: '/mis-obras', color: 'text-raios-primary' },
    { icon: DollarSign, label: 'Ventas', path: '/ventas', color: 'text-green-400' },
    { icon: ShoppingBag, label: 'Compras', path: '/compras', color: 'text-raios-tertiary' },
    { icon: Heart, label: 'Favoritos', path: '/favoritos', color: 'text-pink-400' },
  ];

  const modal_content = (
    <AnimatePresence>
      {is_open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-3 md:p-4 lg:p-8">
          {/* Overlay */}
          <motion.div
            variants={overlay_variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute inset-0 bg-raios-secondary/85 backdrop-blur-md"
            onClick={on_close}
          />

          {/* Panel - Responsive */}
          <GlassSurface
            as_motion
            variants={modal_variants}
            initial="hidden"
            animate="visible"
            exit="exit"
            variant="surface"
            shadow="float"
            className="
              relative w-full rounded-xl overflow-hidden
              max-w-[95vw] md:max-w-md lg:max-w-4xl xl:max-w-5xl
              max-h-[90vh] lg:max-h-[85vh]
            "
          >
            {/* Botón cerrar - Absoluto */}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={on_close}
              className="
                absolute top-3 right-3 lg:top-4 lg:right-4 z-10
                p-2 rounded-lg
                text-raios-text-support hover:text-raios-text-high
                hover:bg-white/10 transition-colors
                min-w-[44px] min-h-[44px] flex items-center justify-center
              "
              aria-label="Cerrar"
            >
              <X size={20} strokeWidth={2} />
            </motion.button>

            {/* ============ MOBILE/TABLET LAYOUT ============ */}
            <div className="lg:hidden flex flex-col max-h-[90vh]">
              {/* Header - Usuario o Branding */}
              <div className="p-3 md:p-5 border-b border-raios-text-support/10 flex-shrink-0">
                {is_authenticated && default_user ? (
                  <div className="flex items-start gap-4 pr-10">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      {default_user.avatar_url ? (
                        <img
                          src={get_avatar_url(default_user.avatar_url, AVATAR_SIZES.menu)}
                          alt={default_user.name}
                          className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-raios-primary/20 flex items-center justify-center">
                          <User size={24} className="text-raios-primary" />
                        </div>
                      )}
                      {default_user.is_verified && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-raios-primary rounded-full flex items-center justify-center">
                          <Check size={12} className="text-white" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-mono text-lg text-raios-text-high truncate">
                        {default_user.name}
                      </h3>
                      <p className="text-sm text-raios-text-support truncate">
                        @{default_user.username}
                      </p>
                      {default_user.is_artist && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-raios-primary/20 text-raios-primary rounded">
                          Artista
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  /* No autenticado - Solo branding */
                  <div className="flex items-center gap-2 pr-10">
                    <span className="font-mono text-xl font-bold text-raios-text-high">
                      RAIOS
                    </span>
                    <span className="font-mono text-xs text-raios-primary tracking-widest">
                      ART
                    </span>
                  </div>
                )}
              </div>

              {/* Content scrollable */}
              <div className="flex-1 overflow-y-auto">
                {/* Banner artista: para todos los usuarios autenticados sin setup completo */}
                {is_authenticated && !profile_setup_complete && (
                  <div className="p-3 md:p-5 border-b border-raios-text-support/10">
                    <div className="relative">
                      <motion.button
                        data-testid="artist-setup-banner"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handle_navigate('/artista/setup')}
                        className="w-full flex items-center gap-3 p-3 pr-10 rounded-xl bg-raios-primary/15 border border-raios-primary/30 hover:bg-raios-primary/25 transition-colors text-left"
                      >
                        <AlertCircle size={18} className="text-raios-primary flex-shrink-0" strokeWidth={2} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-mono text-raios-text-high leading-tight">
                            ¿Quieres vender tus obras?
                          </p>
                          <p className="text-xs text-raios-text-support mt-0.5">
                            Configura tu perfil artista para comenzar
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-raios-primary flex-shrink-0" strokeWidth={2} />
                      </motion.button>
                      {/* Dismiss */}
                      <button
                        data-testid="artist-banner-dismiss"
                        onClick={(e) => {
                          e.stopPropagation();
                          localStorage.setItem('raios_artist_banner_dismissed', 'true');
                          window.dispatchEvent(new CustomEvent('raios_banner_dismissed'));
                        }}
                        className="absolute top-1 right-1 p-1.5 rounded-lg text-raios-text-support hover:text-raios-text-high hover:bg-white/10 transition-colors"
                        aria-label="No me interesa"
                      >
                        <X size={14} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                )}

                {/* Sección NAVEGAR - Siempre visible */}
                <div className="p-3 md:p-5 border-b border-raios-text-support/10">
                  <p className="text-xs text-raios-text-support uppercase tracking-wider mb-3 font-mono">
                    Navegar
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {nav_items_with_icons.map((item) => (
                      <motion.button
                        key={item.path}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handle_navigate(item.path)}
                        className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left min-h-[44px]"
                      >
                        <item.icon size={20} className={item.color} strokeWidth={2} />
                        <span className="text-sm text-raios-text-high">
                          {item.label}
                        </span>
                      </motion.button>
                    ))}
                  </div>

                  {/* CTA Publicar Obra - Prominente, después de navegación */}
                  {is_authenticated && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handle_navigate('/publicar')}
                      className="w-full mt-4 flex items-center justify-center gap-2 p-3.5 bg-raios-primary text-white rounded-lg font-mono text-sm font-medium hover:bg-raios-primary/90 transition-colors min-h-[48px] shadow-lg shadow-raios-primary/25"
                    >
                      <Plus size={20} strokeWidth={2.5} />
                      Publicar Obra
                    </motion.button>
                  )}
                </div>

                {/* Solo para usuarios autenticados */}
                {is_authenticated && default_user && (
                  <>
                    {/* Stats (solo artistas) */}
                    {default_user.is_artist && (
                      <div className="p-3 md:p-5 border-b border-raios-text-support/10">
                        <p className="text-xs text-raios-text-support uppercase tracking-wider mb-3 font-mono">
                          Stats
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { value: default_stats.obras, label: 'Obras' },
                            { value: default_stats.ventas, label: 'Ventas' },
                            { value: default_stats.likes, label: 'Likes' },
                          ].map((stat) => (
                            <GlassSurface
                              key={stat.label}
                              variant="light"
                              shadow="none"
                              className="p-3 rounded-lg text-center"
                            >
                              <p className="font-mono text-lg text-raios-text-high">
                                {stat.value}
                              </p>
                              <p className="text-xs text-raios-text-support">
                                {stat.label}
                              </p>
                            </GlassSurface>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Acciones principales - GESTIONAR */}
                    <div className="p-3 md:px-5">
                      <p className="text-xs text-raios-text-support uppercase tracking-wider mb-3 font-mono">
                        Gestionar
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {main_actions.map((action) => (
                          <motion.button
                            key={action.path}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handle_navigate(action.path)}
                            className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left min-h-[44px]"
                          >
                            <action.icon size={20} className={action.color} strokeWidth={2} />
                            <span className="text-sm text-raios-text-high">
                              {action.label}
                            </span>
                          </motion.button>
                        ))}
                      </div>
                    </div>

                    {/* Ver Perfil */}
                    <div className="px-3 pb-2 md:px-5">
                      <motion.button
                        whileHover={{ scale: 1.01, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handle_navigate('/perfil')}
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors min-h-[44px]"
                      >
                        <span className="text-sm text-raios-text-high">Ver perfil completo</span>
                        <ChevronRight size={18} className="text-raios-text-support" strokeWidth={2} />
                      </motion.button>
                    </div>
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="p-3 md:p-5 border-t border-raios-text-support/10 mt-auto flex-shrink-0">
                {is_authenticated ? (
                  <div className="flex items-center justify-between">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handle_navigate('/configuracion')}
                      className="flex items-center gap-2 p-2 rounded-lg text-raios-text-support hover:text-raios-text-high hover:bg-white/5 transition-colors min-h-[44px]"
                    >
                      <Settings size={18} strokeWidth={2} />
                      <span className="text-sm">Configuración</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={on_logout}
                      className="flex items-center gap-2 p-2 rounded-lg text-raios-text-support hover:text-red-400 hover:bg-white/5 transition-colors min-h-[44px]"
                    >
                      <LogOut size={18} strokeWidth={2} />
                      <span className="text-sm">Salir</span>
                    </motion.button>
                  </div>
                ) : (
                  /* No autenticado - Botón de login */
                  <Button
                    variant="primary"
                    size="md"
                    on_click={on_login}
                    class_name="w-full"
                  >
                    Iniciar Sesión
                  </Button>
                )}
              </div>
            </div>

            {/* ============ DESKTOP LAYOUT (lg+) ============ */}
            <div className="hidden lg:block p-6 xl:p-8 overflow-y-auto">
              {is_authenticated && default_user ? (
                <>
                  {/* Fila 1: Usuario + Stats */}
                  <div className="flex items-start gap-8 xl:gap-12">
                    {/* Usuario + Ver perfil */}
                    <div className="flex-shrink-0">
                      <div className="flex items-center gap-4 mb-4">
                        {/* Avatar */}
                        <div className="relative">
                          {default_user.avatar_url ? (
                            <img
                              src={get_avatar_url(default_user.avatar_url, AVATAR_SIZES.menu_xl)}
                              alt={default_user.name}
                              className="w-16 h-16 xl:w-20 xl:h-20 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-16 h-16 xl:w-20 xl:h-20 rounded-xl bg-raios-primary/20 flex items-center justify-center">
                              <User size={28} className="text-raios-primary" />
                            </div>
                          )}
                          {default_user.is_verified && (
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-raios-primary rounded-full flex items-center justify-center">
                              <Check size={12} className="text-white" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="min-w-0">
                          <h3 className="font-mono text-lg xl:text-xl text-raios-text-high">
                            {default_user.name}
                          </h3>
                          <p className="text-sm text-raios-text-support">
                            @{default_user.username}
                          </p>
                          {default_user.is_artist && (
                            <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-raios-primary/20 text-raios-primary rounded">
                              Artista
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Botón perfil */}
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handle_navigate('/perfil')}
                        className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-raios-text-support/20 text-raios-text-support hover:text-raios-text-high hover:border-raios-primary transition-colors text-sm"
                      >
                        Ver perfil
                        <ChevronRight size={16} strokeWidth={2} />
                      </motion.button>
                    </div>

                    {/* Separador vertical */}
                    <div className="w-px self-stretch bg-raios-text-support/10" />

                    {/* Stats (solo artistas) */}
                    {default_user.is_artist && (
                      <div className="flex-1">
                        <p className="text-xs text-raios-text-support uppercase tracking-wider mb-3 font-mono">
                          Stats
                        </p>
                        <div className="flex gap-4">
                          {[
                            { value: default_stats.obras, label: 'Obras' },
                            { value: default_stats.ventas, label: 'Ventas' },
                            { value: default_stats.likes, label: 'Likes' },
                          ].map((stat) => (
                            <GlassSurface
                              key={stat.label}
                              variant="light"
                              shadow="none"
                              className="p-4 xl:p-5 rounded-lg text-center min-w-[90px] xl:min-w-[100px]"
                            >
                              <p className="font-mono text-2xl xl:text-3xl text-raios-text-high">
                                {stat.value}
                              </p>
                              <p className="text-xs text-raios-text-support mt-1">
                                {stat.label}
                              </p>
                            </GlassSurface>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Banner artista - desktop */}
                  {!profile_setup_complete && (
                    <div className="mt-5 relative">
                      <motion.button
                        data-testid="artist-setup-banner"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handle_navigate('/artista/setup')}
                        className="w-full flex items-center gap-3 p-3.5 pr-10 rounded-xl bg-raios-primary/15 border border-raios-primary/30 hover:bg-raios-primary/25 transition-colors text-left"
                      >
                        <AlertCircle size={18} className="text-raios-primary flex-shrink-0" strokeWidth={2} />
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm text-raios-text-high">
                            ¿Quieres vender tus obras?
                          </p>
                          <p className="text-xs text-raios-text-support mt-0.5">
                            Configura tu perfil artista para comenzar a vender en RAIOS
                          </p>
                        </div>
                        <ChevronRight size={16} className="text-raios-primary flex-shrink-0" strokeWidth={2} />
                      </motion.button>
                      <button
                        data-testid="artist-banner-dismiss"
                        onClick={(e) => {
                          e.stopPropagation();
                          localStorage.setItem('raios_artist_banner_dismissed', 'true');
                          window.dispatchEvent(new CustomEvent('raios_banner_dismissed'));
                        }}
                        className="absolute top-1.5 right-1.5 p-1.5 rounded-lg text-raios-text-support hover:text-raios-text-high hover:bg-white/10 transition-colors"
                        aria-label="No me interesa"
                      >
                        <X size={14} strokeWidth={2} />
                      </button>
                    </div>
                  )}

                  {/* Fila 2: Gestionar + Nueva Obra */}
                  <div className="mt-6 pt-6 border-t border-raios-text-support/10">
                    <div className="flex items-end justify-between gap-6">
                      {/* Acciones en grid */}
                      <div className="flex-1">
                        <p className="text-xs text-raios-text-support uppercase tracking-wider mb-3 font-mono">
                          Gestionar
                        </p>
                        <div className="grid grid-cols-4 gap-3">
                          {main_actions.map((action) => (
                            <motion.button
                              key={action.path}
                              whileHover={{ scale: 1.03, y: -2 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => handle_navigate(action.path)}
                              className="flex flex-col items-center justify-center gap-2 p-4 xl:p-5 rounded-xl bg-white/5 hover:bg-white/10 transition-all text-center"
                            >
                              <action.icon size={24} className={action.color} strokeWidth={2} />
                              <span className="text-sm text-raios-text-high">
                                {action.label}
                              </span>
                            </motion.button>
                          ))}
                        </div>
                      </div>

                      {/* CTA Nueva Obra (artistas) */}
                      {default_user.is_artist && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handle_navigate('/publicar')}
                          className="flex-shrink-0 flex items-center justify-center gap-2 px-6 py-4 bg-raios-primary text-white rounded-lg font-mono text-sm hover:bg-raios-primary/90 transition-colors"
                        >
                          <Plus size={18} />
                          Nueva Obra
                        </motion.button>
                      )}
                    </div>
                  </div>

                  {/* Footer: Configuración + Cerrar sesión */}
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-raios-text-support/10">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handle_navigate('/configuracion')}
                      className="flex items-center gap-3 px-5 py-3 rounded-lg bg-white/5 text-raios-text-high hover:bg-white/10 transition-colors"
                    >
                      <Settings size={20} strokeWidth={2} />
                      <span className="text-sm font-medium">Configuración</span>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={on_logout}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-raios-text-support hover:text-red-400 hover:bg-red-400/10 transition-colors"
                    >
                      <LogOut size={18} strokeWidth={2} />
                      <span className="text-sm">Cerrar sesión</span>
                    </motion.button>
                  </div>
                </>
              ) : (
                /* Desktop no autenticado */
                <div className="text-center py-8">
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <span className="font-mono text-2xl font-bold text-raios-text-high">
                      RAIOS
                    </span>
                    <span className="font-mono text-sm text-raios-primary tracking-widest">
                      ART
                    </span>
                  </div>
                  <p className="text-raios-text-support mb-6">
                    Inicia sesión para acceder a tu cuenta
                  </p>
                  <Button
                    variant="primary"
                    size="lg"
                    on_click={on_login}
                  >
                    Iniciar Sesión
                  </Button>
                </div>
              )}
            </div>
          </GlassSurface>
        </div>
      )}
    </AnimatePresence>
  );

  // Renderizar en portal (SSR-safe)
  if (typeof window === 'undefined') return null;
  return createPortal(modal_content, document.body);
}

export default MenuPanel;
